/**
 * This module contains prop-type definitions for objects that are specific to
 * the trimet.org application
 */
import PropTypes from 'prop-types';
import {
  DESTINATION_LOCATION_TYPE,
  ORIGIN_LOCATION_TYPE,
} from '#/shared/constants';

/** Type for React `ref` object that is assigned a DOM Element value */
export const elementRefType = PropTypes.shape({
  current: PropTypes.instanceOf(
    // handling for SSR since Element is only present in browser
    typeof Element === 'undefined' ? () => {} : Element
  ),
});

/** Maps to schema in project `geocoder-config` file */
export const geocoderConfigType = PropTypes.shape({
  baseUrl: PropTypes.string,
  boundary: PropTypes.shape({
    rect: PropTypes.shape({
      minLon: PropTypes.number,
      maxLon: PropTypes.number,
      minLat: PropTypes.number,
      maxLat: PropTypes.number,
    }),
  }),
  maxNearbyStops: PropTypes.number,
  type: PropTypes.string.isRequired,
});

/** Device retrieved geolocation as stored in the Redux state */
export const geolocationType = PropTypes.shape({
  coords: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  error: PropTypes.shape({
    message: PropTypes.string,
    code: PropTypes.number,
  }),
  fetching: PropTypes.bool,
  outOfBounds: PropTypes.bool,
});

/** String indicating location type in the context of a transit trip */
export const locationCategoryType = PropTypes.oneOf([
  DESTINATION_LOCATION_TYPE,
  ORIGIN_LOCATION_TYPE,
]);

/**
 * Location object format used throughout the app to describe locations such
 * the user's current location and trip origin and destinations
 */
export const locationType = PropTypes.shape({
  /** Name of the location, often an address */
  name: PropTypes.string,
  /** Latitude of the location */
  lat: PropTypes.number.isRequired,
  /** Longitude of the location */
  lon: PropTypes.number.isRequired,
  /** Region of the location, often a state */
  region: PropTypes.string,
});

/** Transit stop data derived from results of `/stops` end point */
export const stopType = PropTypes.shape({
  desc: PropTypes.string,
  dir: PropTypes.string,
  feetDistance: PropTypes.number,
  id: PropTypes.string,
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  locid: PropTypes.number,
  lon: PropTypes.number,
  metersDistance: PropTypes.number,
  name: PropTypes.string,
  seq: PropTypes.number,
  stopId: PropTypes.string,
  tp: PropTypes.bool,
});

const routeDirectionType = PropTypes.shape({
  desc: PropTypes.string,
  dir: PropTypes.number,
  stop: PropTypes.arrayOf(stopType),
});

export const simpleRouteType = PropTypes.shape({
  shortName: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  routeColor: PropTypes.string,
  routeSortOrder: PropTypes.number,
});

export const routeType = PropTypes.shape({
  // extend simple route type:
  // https://github.com/facebook/react/issues/18200#issuecomment-593971916
  ...simpleRouteType.propTypes,

  detour: PropTypes.bool,
  dir: PropTypes.arrayOf(routeDirectionType),
  frequentService: PropTypes.bool.isRequired,
  route: PropTypes.number,
  routeSubType: PropTypes.string,
  type: PropTypes.string.isRequired,
  vanityName: PropTypes.string.isRequired,
});

/** Transit arrival derived from results of `/arrivals` end point */
export const arrivalType = PropTypes.shape({
  blockId: PropTypes.number,
  dir: PropTypes.string,
  estimatedTime: PropTypes.number,
  feet: PropTypes.number,
  fullSign: PropTypes.string,
  id: PropTypes.string,
  route: simpleRouteType,
  scheduledTime: PropTypes.number,
  showMilesAway: PropTypes.bool,
  status: PropTypes.string,
  stopFeetDistance: PropTypes.number,
  stopLng: PropTypes.number,
  stopLat: PropTypes.number,
  stopMetersDistance: PropTypes.number,
  stopName: PropTypes.string,
  vanityName: PropTypes.string,
  vehicleId: PropTypes.string,

  // TODO: clean this up value, it's injected after the arrivals response
  //  comes in an sometimes is set as a string and sometimes a number
  stopId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

export const alertType = PropTypes.shape({
  begin: PropTypes.number,
  desc: PropTypes.string.isRequired,
  end: PropTypes.number,
  header_text: PropTypes.string,
  id: PropTypes.number,
  info_link_url: PropTypes.string,
  system_wide_flag: PropTypes.bool,
});

export const simpleStopType = PropTypes.shape({
  // TODO: clean up creation of these object so that the `id` value has
  //  a consistent type
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lat: PropTypes.number.isRequired,
  lon: PropTypes.number.isRequired,
  isFavorite: PropTypes.bool,
});
